import React from 'react';
import { AbsoluteFill, Sequence, useCurrentFrame, interpolate, spring, useVideoConfig } from 'remotion';

// 物理模板专业版 - 牛顿第二定律（横屏 1920×1080）
const THEME = {
  primary: '#6C5CE7',
  primaryLight: '#A29BFE',
  accent: '#FF6B6B',
  velocity: '#4CAF50',
  highlight: '#FFB347',
  bg: 'linear-gradient(135deg, #F3EFFF 0%, #EDE7FF 50%, #FFFFFF 100%)',
  cardShadow: '0 16px 40px rgba(108, 92, 231, 0.18)',
};
export const PhysicsDemoPro: React.FC = () => {
  return (
    <AbsoluteFill style={{ background: THEME.bg }}>
      <DecorativeBackground />

      <Sequence from={0} durationInFrames={150}>
        <IntroScene />
      </Sequence>
      <Sequence from={150} durationInFrames={150}>
        <TitleScene />
      </Sequence>
      <Sequence from={300} durationInFrames={1050}>
        <ExplanationScene />
      </Sequence>
      <Sequence from={1350} durationInFrames={300}>
        <QuizScene />
      </Sequence>
      <Sequence from={1650} durationInFrames={150}>
        <OutroScene />
      </Sequence>
    </AbsoluteFill>
  );
};

const DecorativeBackground: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <AbsoluteFill>
      {[
        { x: 150, y: 200, r: 160, c: THEME.primary, o: 0.06 },
        { x: 1720, y: 280, r: 200, c: THEME.accent, o: 0.05 },
        { x: 280, y: 880, r: 220, c: THEME.primaryLight, o: 0.05 },
        { x: 1680, y: 860, r: 140, c: THEME.highlight, o: 0.06 },
      ].map((c, i) => {
        const float = Math.sin((frame + i * 40) * 0.025) * 12;
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: c.x - c.r,
              top: c.y - c.r + float,
              width: c.r * 2,
              height: c.r * 2,
              borderRadius: '50%',
              background: c.c,
              opacity: c.o,
              filter: 'blur(22px)',
            }}
          />
        );
      })}
    </AbsoluteFill>
  );
};

const IntroScene: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const scale = spring({ frame, fps, config: { damping: 12, stiffness: 120 } });
  const rotate = interpolate(frame, [0, 150], [0, 360], { extrapolateRight: 'clamp' });
  const fadeText = interpolate(frame, [20, 50], [0, 1], { extrapolateRight: 'clamp' });
  const slideY = interpolate(frame, [20, 50], [30, 0], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill style={{ justifyContent: 'center', alignItems: 'center', flexDirection: 'row', gap: 80 }}>
      <div style={{ fontSize: 240, transform: `scale(${scale}) rotate(${rotate * 0.3}deg)` }}>⚛️</div>
      <div style={{ opacity: fadeText, transform: `translateY(${slideY}px)` }}>
        <div style={{ fontSize: 120, fontWeight: 900, color: THEME.primary, letterSpacing: 4 }}>物理</div>
        <div style={{ fontSize: 32, color: '#555', marginTop: 16, letterSpacing: 6 }}>
          PHYSICS · 力与运动
        </div>
      </div>
    </AbsoluteFill>
  );
};

const TitleScene: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const scale = spring({ frame, fps, config: { damping: 14, stiffness: 110 } });
  const lineWidth = interpolate(frame, [30, 80], [0, 420], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill style={{ justifyContent: 'center', alignItems: 'center' }}>
      <div
        style={{
          padding: '72px 120px',
          background: '#fff',
          borderRadius: 32,
          boxShadow: THEME.cardShadow,
          transform: `scale(${scale})`,
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 26, color: THEME.primaryLight, letterSpacing: 8, marginBottom: 16 }}>
          CHAPTER · 02
        </div>
        <div style={{ fontSize: 72, fontWeight: 900, color: THEME.primary, marginBottom: 24 }}>
          牛顿第二定律
        </div>
        <div style={{ fontSize: 42, color: '#333', fontWeight: 700, fontFamily: 'Georgia, serif' }}>
          F = m · a
        </div>
        <div
          style={{
            height: 4,
            width: lineWidth,
            background: `linear-gradient(90deg, ${THEME.primary}, ${THEME.accent})`,
            margin: '24px auto 0',
            borderRadius: 2,
          }}
        />
      </div>
    </AbsoluteFill>
  );
};

const ExplanationScene: React.FC = () => {
  const frame = useCurrentFrame();

  return (
    <AbsoluteFill style={{ padding: 60, flexDirection: 'row', gap: 40, alignItems: 'center' }}>
      {/* 左侧实验可视化 */}
      <div
        style={{
          width: 1200,
          height: 820,
          background: '#fff',
          borderRadius: 24,
          position: 'relative',
          boxShadow: THEME.cardShadow,
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '24px 32px', borderBottom: '1px solid #F0EDFF' }}>
          <div style={{ fontSize: 30, fontWeight: 700, color: THEME.primary }}>
            力学实验 · F = ma 可视化
          </div>
          <div style={{ fontSize: 26, color: '#888', marginTop: 4 }}>
            对 2kg 物体施加 10N 恒力，观察加速运动
          </div>
        </div>
        <ForceExperimentAnimation frame={frame} />
      </div>

      {/* 右侧知识点 */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div style={{ fontSize: 26, color: THEME.primaryLight, letterSpacing: 4, fontWeight: 600 }}>
          KNOWLEDGE POINTS
        </div>
        <div style={{ fontSize: 42, fontWeight: 900, color: '#1a1a1a', lineHeight: 1.2, marginBottom: 12 }}>
          力学三要素一目了然
        </div>

        {[
          { title: '力 F', content: '作用在物体上的推力或拉力', unit: 'N · 牛顿', color: THEME.accent },
          { title: '质量 m', content: '物体所含物质的多少，不随位置改变', unit: 'kg · 千克', color: THEME.primary },
          { title: '加速度 a', content: '速度变化的快慢，方向与合力相同', unit: 'm/s² · 米每二次方秒', color: THEME.velocity },
        ].map((point, i) => {
          const start = i * 200 + 100;
          const opacity = interpolate(frame, [start, start + 30], [0, 1], { extrapolateRight: 'clamp' });
          const x = interpolate(frame, [start, start + 30], [40, 0], { extrapolateRight: 'clamp' });

          return (
            <div
              key={i}
              style={{
                padding: '22px 28px',
                background: '#fff',
                borderRadius: 16,
                opacity,
                transform: `translateX(${x}px)`,
                boxShadow: '0 4px 16px rgba(108, 92, 231, 0.08)',
                borderLeft: `4px solid ${point.color}`,
                display: 'flex',
                alignItems: 'center',
                gap: 20,
              }}
            >
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 16,
                  background: `linear-gradient(135deg, ${point.color}, ${point.color}AA)`,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 26,
                  fontWeight: 900,
                  fontFamily: 'Georgia, serif',
                  flexShrink: 0,
                }}
              >
                {point.title.split(' ')[1]}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 26, fontWeight: 700, color: '#222', marginBottom: 4 }}>
                  {point.title}
                </div>
                <div style={{ fontSize: 22, color: '#666', marginBottom: 4 }}>{point.content}</div>
                <div style={{ fontSize: 20, color: point.color, fontWeight: 600 }}>{point.unit}</div>
              </div>
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

const ForceExperimentAnimation: React.FC<{ frame: number }> = ({ frame }) => {
  const width = 1200;
  const height = 720;
  const groundY = 520;

  // 场景淡入
  const sceneProgress = interpolate(frame, [0, 20], [0, 1], { extrapolateRight: 'clamp' });

  // 物体位置：加速运动 s = 0.5 * a * t², a=5, t 以秒计
  // 刻度线间距为 90px/m，保持换算一致；小球到达 8m（像素 920）后停止
  const a = 5;
  const ballStartX = 200;
  const pxPerMeter = 90;
  const maxDistance = 8; // m
  const tMax = Math.sqrt((2 * maxDistance) / a);
  const tRaw = Math.max(0, frame - 60) / 30;
  const t = Math.min(tRaw, tMax);
  const s = 0.5 * a * t * t;
  const ballX = ballStartX + s * pxPerMeter;
  const ballY = groundY - 45;

  // 箭头进度
  const forceProg = interpolate(frame, [40, 70], [0, 1], { extrapolateRight: 'clamp' });
  const velocityProg = interpolate(frame, [80, 120], [0, 1], { extrapolateRight: 'clamp' });

  // 当前速度 v = a*t
  const velocity = a * t;
  const velArrowLen = Math.min(30 + velocity * 12, 160);

  return (
    <svg width={width} height={height} style={{ display: 'block' }} opacity={sceneProgress}>
      <defs>
        <pattern id="phys-grid" width="60" height="60" patternUnits="userSpaceOnUse">
          <path d="M 60 0 L 0 0 0 60" fill="none" stroke="#F0EDFF" strokeWidth="1" />
        </pattern>
        <linearGradient id="ball-grad" cx="0.3" cy="0.3" r="0.7">
          <stop offset="0%" stopColor={THEME.primaryLight} />
          <stop offset="100%" stopColor={THEME.primary} />
        </linearGradient>
        <linearGradient id="ground-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#E8E0FF" />
          <stop offset="100%" stopColor="#C5B9FF" />
        </linearGradient>
      </defs>

      <rect width={width} height={height} fill="url(#phys-grid)" />

      {/* 地面 */}
      <rect x={80} y={groundY} width={920} height={10} fill="url(#ground-grad)" rx={2} />
      {Array.from({ length: 10 }).map((_, i) => (
        <line
          key={i}
          x1={100 + i * 90}
          y1={groundY + 10}
          x2={80 + i * 90}
          y2={groundY + 30}
          stroke="#C5B9FF"
          strokeWidth="2"
        />
      ))}

      {/* 刻度 */}
      {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
        <g key={i}>
          <line x1={200 + i * 90} y1={groundY} x2={200 + i * 90} y2={groundY + 8} stroke="#666" strokeWidth="1.5" />
          <text x={200 + i * 90} y={groundY + 48} fontSize="16" fill="#666" textAnchor="middle">{i}m</text>
        </g>
      ))}

      {/* 小球阴影 */}
      <ellipse cx={ballX} cy={groundY + 4} rx={42} ry={6} fill="#000" opacity={0.1} />

      {/* 轨迹残影 */}
      {t > 0.2 && [0.8, 0.6, 0.4, 0.2].map((mult, i) => {
        const tTrail = Math.max(0, t - mult * 0.3);
        const sTrail = 0.5 * a * tTrail * tTrail;
        const xTrail = Math.min(ballStartX + sTrail * 35, 920);
        return (
          <circle key={i} cx={xTrail} cy={ballY} r={40 - i * 4} fill={THEME.primaryLight} opacity={0.12 - i * 0.025} />
        );
      })}

      {/* 小球 */}
      <circle cx={ballX} cy={ballY} r={40} fill="url(#ball-grad)" />
      <circle cx={ballX - 12} cy={ballY - 12} r={12} fill="#fff" opacity={0.35} />
      <text x={ballX} y={ballY + 6} fontSize="24" fill="#fff" fontWeight="900" textAnchor="middle">
        2kg
      </text>

      {/* 力箭头 F */}
      {forceProg > 0 && (
        <g opacity={forceProg}>
          <line
            x1={ballX - 60 - 110 * forceProg}
            y1={ballY}
            x2={ballX - 44}
            y2={ballY}
            stroke={THEME.accent}
            strokeWidth="6"
            strokeLinecap="round"
          />
          <polygon
            points={`${ballX - 44},${ballY} ${ballX - 56},${ballY - 8} ${ballX - 56},${ballY + 8}`}
            fill={THEME.accent}
          />
          <rect x={ballX - 162} y={ballY - 42} width={100} height={28} rx={6} fill={THEME.accent} />
          <text x={ballX - 112} y={ballY - 22} fontSize="18" fill="#fff" fontWeight="700" textAnchor="middle">
            F = 10N
          </text>
        </g>
      )}

      {/* 速度箭头 v */}
      {velocityProg > 0 && velArrowLen > 30 && (
        <g opacity={velocityProg}>
          <line
            x1={ballX + 48}
            y1={ballY}
            x2={ballX + 48 + velArrowLen}
            y2={ballY}
            stroke={THEME.velocity}
            strokeWidth="5"
            strokeLinecap="round"
          />
          <polygon
            points={`${ballX + 48 + velArrowLen + 12},${ballY} ${ballX + 48 + velArrowLen},${ballY - 8} ${ballX + 48 + velArrowLen},${ballY + 8}`}
            fill={THEME.velocity}
          />
          {(() => {
            const labelW = 140;
            const labelX = Math.min(ballX + 50, width - labelW - 12);
            return (
              <>
                <rect x={labelX} y={ballY - 42} width={labelW} height={28} rx={6} fill={THEME.velocity} />
                <text x={labelX + labelW / 2} y={ballY - 22} fontSize="18" fill="#fff" fontWeight="700" textAnchor="middle">
                  v = {velocity.toFixed(1)} m/s
                </text>
              </>
            );
          })()}
        </g>
      )}

      {/* 公式面板 */}
      {frame > 160 && (
        <g opacity={interpolate(frame, [160, 200], [0, 1], { extrapolateRight: 'clamp' })}>
          <rect x={width - 320} y={80} width={260} height={160} rx={16} fill="#fff" stroke={THEME.primary} strokeWidth="2" />
          <text x={width - 190} y={120} fontSize="26" fill="#888" textAnchor="middle" fontWeight="600">
            牛顿第二定律
          </text>
          <text x={width - 190} y={172} fontSize="36" fill={THEME.primary} fontWeight="900" textAnchor="middle">
            F = ma
          </text>
          <line x1={width - 290} y1={184} x2={width - 90} y2={184} stroke="#F0EDFF" strokeWidth="1" />
          <text x={width - 190} y={218} fontSize="22" fill="#333" textAnchor="middle" fontWeight="600">
            10N = 2kg × 5m/s²
          </text>
        </g>
      )}

      {/* 动态数据面板 */}
      {frame > 240 && (
        <g opacity={interpolate(frame, [240, 280], [0, 1], { extrapolateRight: 'clamp' })}>
          <rect x={60} y={80} width={330} height={116} rx={14} fill={THEME.primary} />
          <text x={220} y={110} fontSize="22" fill="#fff" opacity={0.8} textAnchor="middle">
            实时数据 · LIVE
          </text>
          <text x={220} y={147} fontSize="24" fill="#fff" fontWeight="900" textAnchor="middle">
            v = {velocity.toFixed(2)} m/s
          </text>
          <text x={228} y={182} fontSize="24" fill="#fff" opacity={0.85} textAnchor="middle">
            t = {t.toFixed(2)} s · s = {s.toFixed(2)} m
          </text>
        </g>
      )}
    </svg>
  );
};

const QuizScene: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const scale = spring({ frame, fps, config: { damping: 14, stiffness: 110 } });
  const answerOpacity = interpolate(frame, [120, 160], [0, 1], { extrapolateRight: 'clamp' });
  const answerY = interpolate(frame, [120, 160], [20, 0], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill style={{ justifyContent: 'center', alignItems: 'center' }}>
      <div
        style={{
          padding: '64px 96px',
          background: '#fff',
          borderRadius: 28,
          boxShadow: THEME.cardShadow,
          transform: `scale(${scale})`,
          minWidth: 960,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 32 }}>
          <div
            style={{
              padding: '8px 20px',
              background: THEME.primary,
              color: '#fff',
              borderRadius: 20,
              fontSize: 26,
              fontWeight: 700,
            }}
          >
            Q · 互动测验
          </div>
          <div style={{ fontSize: 26, color: '#888' }}>应用 F = ma 解题</div>
        </div>

        <div style={{ fontSize: 34, fontWeight: 700, color: '#1a1a1a', marginBottom: 40, lineHeight: 1.4 }}>
          质量 <span style={{ color: THEME.primary }}>3kg</span> 的物体受到
          <span style={{ color: THEME.accent }}> 15N </span>的力，加速度是多少？
        </div>

        <div
          style={{
            opacity: answerOpacity,
            transform: `translateY(${answerY}px)`,
            padding: '28px 32px',
            background: 'linear-gradient(135deg, #E8F5E9 0%, #F1F8E9 100%)',
            borderRadius: 16,
            borderLeft: '6px solid #4CAF50',
          }}
        >
          <div style={{ fontSize: 28, color: '#4CAF50', fontWeight: 700, marginBottom: 8 }}>正确答案</div>
          <div style={{ fontSize: 28, color: '#2E7D32', fontWeight: 700, fontFamily: 'Georgia, serif' }}>
            a = F / m = 15N ÷ 3kg = 5 m/s²
          </div>
        </div>
      </div>
    </AbsoluteFill>
  );
};

const OutroScene: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const scale = spring({ frame, fps, config: { damping: 12, stiffness: 110 } });
  const textOpacity = interpolate(frame, [30, 70], [0, 1], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill style={{ justifyContent: 'center', alignItems: 'center', flexDirection: 'row', gap: 80 }}>
      <div style={{ fontSize: 220, transform: `scale(${scale})` }}>🚀</div>
      <div style={{ opacity: textOpacity }}>
        <div style={{ fontSize: 100, fontWeight: 900, color: THEME.primary }}>掌握了！</div>
        <div style={{ fontSize: 30, color: '#555', marginTop: 16, letterSpacing: 4 }}>
          继续探索物理世界的奥秘
        </div>
      </div>
    </AbsoluteFill>
  );
};
